import React, { useState, useMemo } from 'react';
import Highcharts from 'highcharts';
import SunburstModule from 'highcharts/modules/sunburst';
import HighchartsReact from 'highcharts-react-official';
import { Translate } from 'react-localize-redux';
import SearchTextField from '../../../components/molecules/SearchTextField/SearchTextField';
import { PerformanceColor } from '../../consts';
import css from './Sunburst.css';

if (!Highcharts.seriesTypes.sunburst) SunburstModule(Highcharts);

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANOMALY_ANALYSIS';

const LEVELS = [
    {
        level: 1,
        color: 'transparent',
        dataLabels: {
            enabled: true,
            style: { fontSize: '14px', color: 'var(--gray500)', fontWeight: '400' },
        },
    },
    {
        level: 2,
        dataLabels: {
            enabled: true,
            style: { fontSize: '13px', fontWeight: '400' },
        },
    },
];

const isRingNode = nodeId => nodeId === 'root' || nodeId.startsWith('ring_');

// TODO: remove mock data
const MOCK_DATA = [
    { id: 'root', name: '', parent: '', color: 'transparent' },
    { id: 'ring_app', name: 'APPS', parent: 'root', color: 'transparent' },
    { id: 'ring_app|app:Subway Surfers', name: 'Subway Surfers', parent: 'ring_app', value: 120000, color: PerformanceColor.GOOD },
    { id: 'ring_app|app:Candy Crush', name: 'Candy Crush', parent: 'ring_app', value: 95000, color: PerformanceColor.POOR },
    { id: 'ring_app|app:Clash of Clans', name: 'Clash of Clans', parent: 'ring_app', value: 80000, color: PerformanceColor.EXCELLENT },
    { id: 'ring_app|app:PUBG Mobile', name: 'PUBG Mobile', parent: 'ring_app', value: 60000, color: PerformanceColor.NEUTRAL },
    { id: 'ring_source', name: 'SOURCE', parent: 'root', color: 'transparent' },
    { id: 'ring_source|source:Facebook', name: 'Facebook', parent: 'ring_source', value: 150000, color: PerformanceColor.EXCELLENT },
    { id: 'ring_source|source:Google', name: 'Google', parent: 'ring_source', value: 110000, color: PerformanceColor.GOOD },
    { id: 'ring_source|source:TikTok', name: 'TikTok', parent: 'ring_source', value: 75000, color: PerformanceColor.BELOW_AVG },
    { id: 'ring_source|source:Apple Search Ads', name: 'Apple Search Ads', parent: 'ring_source', value: 40000, color: PerformanceColor.NEUTRAL },
];

const filterDataBySearch = (data, searchTerm) => {
    if (!searchTerm) return data;
    const lower = searchTerm.toLowerCase();
    const matchingIds = new Set(
        data
            .filter(node => !isRingNode(node.id) && node.name?.toLowerCase().includes(lower))
            .map(node => node.id)
    );
    return data.filter(node => isRingNode(node.id) || matchingIds.has(node.id));
};

const Sunburst = () => {
    const [search, setSearch] = useState('');

    const filteredData = useMemo(() => filterDataBySearch(MOCK_DATA, search), [search]);

    const leafCount = filteredData.filter(node => !isRingNode(node.id)).length;

    const chartOptions = useMemo(() => ({
        chart: { backgroundColor: 'transparent' },
        credits: { enabled: false },
        exporting: { enabled: false },
        title: { text: null },
        tooltip: { headerFormat: '', pointFormat: '{point.name}' },
        series: [
            {
                type: 'sunburst',
                data: filteredData,
                allowDrillToNode: true,
                cursor: 'pointer',
                innerSize: '40%',
                levels: LEVELS,
                dataLabels: { format: '{point.name}' },
            },
        ],
    }), [filteredData]);

    return (
        <div className={css.container}>
            <div className={css.toolbar}>
                <SearchTextField
                    search={search}
                    onSearchChange={setSearch}
                    placeholder={`${TRANSLATION_PREFIX}.SUNBURST.SEARCH_PLACEHOLDER`}
                    className={css.searchBox}
                />
                <div className={css.itemCount}>
                    <Translate id={`${TRANSLATION_PREFIX}.SUNBURST.SHOWING_ITEMS`} data={{ count: leafCount }} />
                </div>
            </div>
            <div className={css.legend}>
                {Object.entries(PerformanceColor).map(([key, color]) => (
                    <div key={key} className={css.legendItem}>
                        <div className={css.legendColor} style={{ backgroundColor: color }} />
                        <Translate id={`${TRANSLATION_PREFIX}.LEGEND.${key}`} />
                    </div>
                ))}
            </div>
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </div>
    );
};

export default Sunburst;
